// Payment-health status page contract. Mirrors the admin backend response of
// GET /payment-health/status (see server KRLY-5903 getPaymentHealthStatus).

export enum IncidentComponent {
  Yazara = "Yazara",
  Rapyd = "Rapyd",
  Internal = "Internal",
}

export enum IncidentStatus {
  Open = "open",
  Monitoring = "monitoring",
  Resolved = "resolved",
  FalsePositive = "false_positive",
}

export enum IncidentSeverity {
  Degraded = "degraded",
  PartialOutage = "partial_outage",
  MajorOutage = "major_outage",
}

export enum IncidentResolutionType {
  Auto = "auto",
  Manual = "manual",
  FalsePositive = "false_positive",
}

export enum ComponentState {
  Operational = "operational",
  Degraded = "degraded",
  PartialOutage = "partial_outage",
  MajorOutage = "major_outage",
}

export type OverallState = ComponentState | "unknown";

export interface IIncidentObservedError {
  errorCode: string;
  event: string;
  peakRatioToBaseline: number;
  eventCount: number;
  firstSeen: string;
  lastSeen: string;
}

export interface IncidentSummary {
  id: string;
  component: IncidentComponent;
  status: IncidentStatus;
  severity: IncidentSeverity;
  startedAt: string;
  lastObservedAt: string;
  resolvedAt?: string;
  affectedMerchantCount: number;
  affectedDeviceCount: number;
  affectedAppVersions: string[];
  observedErrors: IIncidentObservedError[];
  resolutionType?: IncidentResolutionType;
  resolvedBy?: string;
  resolutionNote?: string;
}

export interface MonitorStatus {
  key: string;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  mixpanelFetchOk: boolean;
  stale: boolean;
  healthy: boolean;
  lastError?: string | null;
}

export interface ComponentStatus {
  component: IncidentComponent;
  state: ComponentState;
  activeIncidentId?: string;
}

export interface PaymentHealthStatusResponse {
  overallState: OverallState;
  monitor: MonitorStatus;
  components: ComponentStatus[];
  activeIncidents: IncidentSummary[];
  recentIncidents: IncidentSummary[];
  generatedAt: string;
}

export const LIVE_INCIDENT_STATUSES = [
  IncidentStatus.Open,
  IncidentStatus.Monitoring,
];

// Uptime timeline contract. Mirrors GET /payment-health/history
// (see server KRLY-5903 getPaymentHealthHistory). Runs are aggregated into
// buckets server-side so we never ship every raw check to the browser.
export type RunStatus = "operational" | "unknown" | "incident";
export type BucketStatus = RunStatus | "missing";

export interface HealthHistoryBucket {
  start: string;
  end: string;
  status: BucketStatus;
  operational: number;
  incident: number;
  unknown: number;
  observed: number;
  expected: number;
}

export interface HealthHistorySummary {
  total: number;
  operational: number;
  unknown: number;
  incident: number;
  uptimePct: number;
}

export interface PaymentHealthHistoryResponse {
  buckets: HealthHistoryBucket[];
  summary: HealthHistorySummary;
  windowStart: string;
  windowEnd: string;
  intervalMinutes: number;
  bucketMinutes: number;
  retentionDays: number;
  generatedAt: string;
}

// Dev-only event simulator contract. Mirrors POST /payment-health/simulate-events
// (see server KRLY-5903 simulatePaymentEvents).
export interface SimulatePaymentEventsInput {
  event: string;
  count?: number;
  errorCode?: string;
  merchantCount?: number;
  deviceCount?: number;
  appVersion?: string;
  extraProperties?: Record<string, string | number | boolean>;
}

export interface SimulatePaymentEventsResult {
  sent: number;
  event: string;
  projectId: string;
  distinctMerchants: number;
  distinctDevices: number;
  note: string;
}
